"use client";
import { useEffect, useMemo, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { toast } from "sonner";
import { useAuth } from "@/features/auth/contexts/AuthContext";
import { useCustomerMarketplace as useMarketplace } from "@/features/shop/customer/customer-shop.queries";
import {
  useAddToCustomerCart,
  useSyncLocalCustomerCart,
} from "@/features/shop/customer/customer-shop.mutations";
import type { CustomerProduct } from "@/features/shop/types/customer-shop";
import { CATEGORY_OPTIONS } from "@/features/shop/components/shop-page.constants";
import type {
  CategoryCountItem,
  CategoryOption,
} from "@/features/shop/components/shop-page.types";
import { ShopPageHeader } from "@/features/shop/components/ShopPageHeader";
import { ShopFiltersCard } from "@/features/shop/components/ShopFiltersCard";
import { ProductSection } from "@/features/shop/components/ProductSection";

export default function ShopPageContent() {
  const { userAccount, loading } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const searchValue = searchParams.get("search")?.trim() ?? "";
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const addToCart = useAddToCustomerCart();
  const { mutate: syncLocalCart } = useSyncLocalCustomerCart();

  const selectedCategory = useMemo<CategoryOption>(() => {
    const categoryParam = searchParams.get("category") ?? "";
    if (
      categoryParam &&
      CATEGORY_OPTIONS.includes(
        categoryParam as (typeof CATEGORY_OPTIONS)[number],
      )
    ) {
      return categoryParam as (typeof CATEGORY_OPTIONS)[number];
    }
    return "";
  }, [searchParams]);

  useEffect(() => {
    if (!loading && userAccount?.role === "customer") {
      syncLocalCart();
    }
  }, [loading, syncLocalCart, userAccount?.role]);

  const marketplace = useMarketplace({
    search: searchValue || undefined,
  });

  const products = useMemo(
    () => marketplace.data?.data?.products ?? [],
    [marketplace.data?.data?.products],
  );
  const filteredProducts = useMemo(() => {
    if (!selectedCategory) return products;
    return products.filter((product) => product.category === selectedCategory);
  }, [products, selectedCategory]);

  const featuredProducts = useMemo(
    () => filteredProducts.slice(0, 4),
    [filteredProducts],
  );
  const categoryCounts = useMemo<CategoryCountItem[]>(() => {
    const counts = new Map<string, number>();
    products.forEach((product) => {
      counts.set(product.category, (counts.get(product.category) ?? 0) + 1);
    });
    return CATEGORY_OPTIONS.filter(Boolean).map((category) => ({
      category,
      count: counts.get(category) ?? 0,
    }));
  }, [products]);

  function handleQuantityChange(productId: string, value: string) {
    const parsed = Number(value);
    setQuantities((current) => ({
      ...current,
      [productId]: Number.isFinite(parsed) && parsed > 0 ? parsed : 1,
    }));
  }

  function updateCategory(category: CategoryOption) {
    if (!category) {
      router.push("/shop");
      return;
    }

    router.push(`/shop?category=${category}`);
  }

  async function handleAddToCart(product: CustomerProduct) {
    try {
      const shopId =
        typeof product.shopId === "object" &&
        product.shopId &&
        "_id" in product.shopId
          ? product.shopId._id
          : String(product.shopId);

      await addToCart.mutateAsync({
        shopId,
        productId: product._id,
        quantity: quantities[product._id] ?? 1,
      });

      toast.success(`${product.name} added to cart.`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to add item to cart",
      );
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <main className="mx-auto max-w-7xl px-4 py-6 md:px-6 md:py-8">
        <ShopPageHeader
          selectedCategory={selectedCategory}
          searchQuery={searchValue}
          className="animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both"
        />

        <div className="grid gap-6 lg:grid-cols-[300px_1fr]">
          <aside className="animate-in fade-in slide-in-from-left-4 space-y-4 duration-500 delay-100 fill-mode-both">
            <ShopFiltersCard
              selectedCategory={selectedCategory}
              categoryCounts={categoryCounts}
              onCategoryChange={updateCategory}
            />
          </aside>

          <section className="animate-in fade-in slide-in-from-bottom-4 space-y-5 duration-500 delay-150 fill-mode-both">
            {/* {featuredProducts.length > 0 ? (
              <ProductSection
                title="Featured Products"
                description="Fresh additions from active farm shops."
                products={featuredProducts}
                quantities={quantities}
                onQuantityChange={handleQuantityChange}
                onAddToCart={handleAddToCart}
                isPending={addToCart.isPending}
                loading={false}
                emptyMessage="No featured products available yet."
                gridClassName="grid gap-4 md:grid-cols-2 xl:grid-cols-4"
                cardCount={4}
                showQuantity
              />
            ) : null} */}

            <ProductSection
              title={searchValue ? `Results for "${searchValue}"` : "All Products"}
              description={
                searchValue
                  ? `Products matching "${searchValue}".`
                  : "Everything in the marketplace."
              }
              products={filteredProducts}
              quantities={quantities}
              onQuantityChange={handleQuantityChange}
              onAddToCart={handleAddToCart}
              isPending={addToCart.isPending}
              loading={marketplace.isLoading}
              error={marketplace.error}
              emptyMessage="No products match the selected filters."
              gridClassName="grid gap-4 md:grid-cols-2 xl:grid-cols-4"
              cardCount={8}
            />
          </section>
        </div>
      </main>
    </div>
  );
}
